<template>
  <v-dialog
    :content-class="'cves-amount-right'"
    :value="internalValue"
    :width="width"
    @click:outside='onCloseClickButton'
    @keydown.esc="onCloseClickButton"
  >
    <div class='cves-amount-popup'>
      <div class='cves-amount-popup__action'>
      <span class='close_btn' @click='onCloseClickButton'>
        <span class="material-icons-outlined">
          close
        </span>
      </span>
      </div>
      <div class='cves-amount-popup__content'>
        <div class='cves-amount-popup__content__title'>
          <span class='cves-amount-popup__content__title__icon'>
            <v-img v-if="technologyIcon" :src='technologyIcon'/>
            <span v-else class="material-icons-outlined widgets">
              widgets
            </span>
          </span>
          <span class='cves-amount-popup__content__title__name'>
            {{ formattedNameAndVersion }}
          </span>
        </div>
        <div class='cves-amount-popup__content__description' ref="dialogRef">
          <wombat-table
            class="cves-amount-popup-table"
            :items="formattedCvesList"
            :columns="tableColumns"
            :loadingItems="false"
            :resize="false"
            :columnsReorder="false"
            :itemHeight="58"
            :noResultsMessage="$t('noResultsFound')"
          >
            <component
              slot="row-cell"
              slot-scope="rowCell"
              class="row-cell"
              :is="tableRowCellComponentsHash[rowCell.column.name] || tableRowCellComponentsHash.default"
              :item="rowCell.item"
              :column="rowCell.column"
            />
          </wombat-table>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import technologiesKeys from '@/constants/technologies';
import WombatTable from '@/components/WombatTable/WombatTable.vue';
import { cvesDataColumns, getEmptyColumn } from '@/services/tableColumns';
import WombatRowDefaultCell from '@/components/WombatTable/WombatRowDefaultCell.vue';
// CYN-2151 import CveTrendTableCell from '@/components/tableCells/CveDataTableCells/CveTrendTableCell.vue';
import CveCvssScoreTableCell from '@/components/tableCells/CveDataTableCells/CveCvssScoreTableCell.vue';
import CveNameAndLinkTableCell from '@/components/tableCells/CveDataTableCells/CveNameAndLinkTableCell.vue';
import CveVersionTableCell from '@/components/tableCells/CveDataTableCells/CveVersionTableCell.vue';
import indexDB from '@/assets/tech-metadata/index-db.json';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'CvesAmountPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  components: { WombatTable },
  props: {
    technologyItem: {
      type: Object,
      required: true,
    },
    internalValue: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    tableRowCellComponentsHash() {
      return {
        cve: CveNameAndLinkTableCell,
        // CYN-2151 trendy: CveTrendTableCell,
        cvss: CveCvssScoreTableCell,
        version: CveVersionTableCell,
        default: WombatRowDefaultCell,
      };
    },
    tableColumns() {
      const columns = [
        cvesDataColumns.version,
        cvesDataColumns.cve,
        cvesDataColumns.cvss,
        // CYN-2151 cvesDataColumns.trendy,
      ];
      return [getEmptyColumn(5), ...columns, getEmptyColumn(5)];
    },
    cvesList() {
      const cvesTableItems = [];
      let cveId = 0;
      const versionKeys = this.technologyItem?.cves ? Object.keys(this.technologyItem.cves) : [];
      for (let i = 0; i < versionKeys.length; i += 1) {
        const currentVersion = versionKeys[i];
        const currentCves = this.technologyItem.cves[currentVersion];
        for (let j = 0; j < currentCves.length; j += 1) {
          const currentCve = currentCves[j];
          if (currentCve.name.length > 0) {
            const cve = { name: currentCve.name, url: currentCve.url || '' };
            const trendy = currentCve.trend || false;
            const cvss = { score: 0, version: 'cvss2' };
            if (Object.keys(currentCve).includes('cvss3') && currentCve.cvss3 && Object.keys(currentCve.cvss3).length > 0) {
              if (!Object.keys(currentCve.cvss3).includes('None')) {
                cvss.score = currentCve.cvss3;
                cvss.version = 'cvss3';
              } else {
                cvss.score = currentCve.cvss2;
              }
            } else if (Object.keys(currentCve).includes('cvss2')) {
              cvss.score = currentCve.cvss2;
            }
            cvesTableItems.push({
              id: cveId += 1,
              version: currentVersion || 'N/A',
              trendy,
              cve,
              cvss,
            });
          }
        }
      }
      return cvesTableItems;
    },
    formattedCvesList() {
      const cves = this.cvesList;
      return cves.sort((a, b) => {
        const scoreA = Object.keys(a.cvss.score)[0] || 0;
        const scoreB = Object.keys(b.cvss.score)[0] || 0;

        return scoreB - scoreA;
      });
    },

    width() {
      return 500;
    },
    formattedNameAndVersion() {
      return this.technologyItem.name?.replace('/', ' ') || 'N/A';
    },
    technologyFormattedName() {
      const techName = this.technologyItem.name?.split('/')[0];
      const formattedTechName = techName.toLowerCase().replaceAll(' ', '-').replace('.', '');
      if (Object.keys(indexDB).includes(formattedTechName)) {
        if (Object.keys(indexDB[formattedTechName]).includes(technologiesKeys.ref)) {
          return indexDB[formattedTechName][technologiesKeys.ref];
        }
      }
      return formattedTechName;
    },
    technologyIcon() {
      let iconSearch;
      let searchName = this.technologyFormattedName;
      let icon;
      if (this.technologyItem.name) {
        if (Object.keys(indexDB).includes(this.technologyFormattedName)) {
          if (Object.keys(indexDB[this.technologyFormattedName]).includes(technologiesKeys.ref)) {
            searchName = indexDB[this.technologyFormattedName][technologiesKeys.ref];
          }
          iconSearch = indexDB[searchName][technologiesKeys.icon];
        }
        try {
          // eslint-disable-next-line global-require,import/no-dynamic-require
          icon = require(`@/assets/tech-metadata/${iconSearch}`);
        } catch (e) {
          icon = null;
        }
      }
      return icon;
    },
  },
  methods: {
    onCloseClickButton() {
      this.$emit('closed');
      setTimeout(() => {
        this.$refs.dialogRef.scrollTop = 0;
      }, 50);
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.close_btn {
  margin-right: $space-1-5;
  margin-top: calc(#{$space-2} + #{$space-0-5});
  float: right;
  &:hover {
    cursor: pointer;
  }
}
.cves-amount-popup {
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
  &__content {
    display: flex;
    flex-direction: column;
    margin-left: rem(24);
    margin-right: rem(24);
    &__title {
      margin-bottom: rem(19);
      display: flex;
      &__icon {
        .v-image {
          max-width: $space-8;
        }
        .widgets {
          max-width: $space-8;
          padding-top: $space-1-5;
        }
      }
      &__name {
        color: var(--text-primary);
        font-family: $font-family-bold;
        font-size: $space-4;
        font-weight: bold;
        letter-spacing: 0;
        margin-left: $space-2;
        text-transform: capitalize;
        align-self: center;
      }
    }
    &__description {
      font-family: $font-family-regular;
      margin-bottom: $space-8;
      overflow-y: auto;
      max-height: 60vh;
      margin-right: rem(-24);
      padding-right: rem(24);
    }
  }
  .column-column4{
    max-width: $space-1-5;
  }
}
</style>
<style lang='scss'>
@import '~@/assets/styles/design-system';

.cves-amount-right{
  overflow-y: hidden;
}
.cves-amount-popup {
  .column-column0 {
    min-width: $space-1-5;
    max-width: $space-1-5;
  }
  .column-version {
    min-width: rem(145);
    max-width: rem(145);
  }
  .column-cve {
    min-width: rem(193);
    max-width: rem(193);
  }
  .column-cvss {
    min-width: rem(97);
    max-width: rem(97);
  }
  .column-column4 {
    min-width: $space-1-5;
    max-width: $space-1-5;
  }
}
</style>
